import { BankAccountType, Country } from '#sign-up/components/SignUp/types';

// Helper function to get a URL query parameter by name, case-insensitively
export const getParamCaseInsensitive = (name: string): string => {
  const params = new URLSearchParams(globalThis.location.search);
  const lower = name.toLowerCase();
  return Array.from(params.entries()).find(([paramKey]) => paramKey.toLowerCase() === lower)?.[1] ?? '';
};

// Helper function to convert enum string names to numeric values
export const parseEnumValue = (
  enumObj: Record<string, unknown>,
  value: string | number | null | undefined
): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  // If it's a string, look up the enum value by name
  const result = enumObj[value];
  return typeof result === 'number' ? result : null;
};

// Helper function to format date to yyyy-MM-dd for date inputs
export const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

// CountryId is a byte coming from the SignUpModel but an enum coming from OwnerModel
// Normalizes various representations of country IDs to the Country enum
export const normalizeCountryId = (value: unknown): Country | null => {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    return value as Country;
  }

  if (typeof value === 'string') {
    const numericValue = Number.parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
      return numericValue as Country;
    }

    if (value in Country) {
      return Country[value as keyof typeof Country] as Country;
    }
  }

  return null;
};

export const normalizeBankAccountTypeId = (value: unknown): BankAccountType | null => {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    return value as BankAccountType;
  }

  if (typeof value === 'string') {
    const numericValue = Number.parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
      return numericValue as BankAccountType;
    }

    if (value in BankAccountType) {
      return BankAccountType[value as keyof typeof BankAccountType] as BankAccountType;
    }
  }

  return null;
};

// Parses a Canadian trust routing number ("0" + institutionNumber(3) + transitNumber(5))
// back into its component parts for form prefill.
// Example: "012345678" → { institutionNumber: "123", transitNumber: "45678" }
export const parseCanadianRoutingNumber = (
  routingNumber: string | null | undefined
): { institutionNumber: string; transitNumber: string } => {
  if (!routingNumber?.length || routingNumber.length !== 9 || !routingNumber.startsWith('0')) {
    return { institutionNumber: '', transitNumber: '' };
  }
  return {
    institutionNumber: routingNumber.slice(1, 4),
    transitNumber: routingNumber.slice(4),
  };
};
